import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useProjectCreateWizard } from '../ProjectCreateWizardContext'
import { Eye } from 'lucide-react'

import { useClientSearchV2 } from '@/hooks/useClientSearchV2'

function isNonNull<T>(v: T | null | undefined): v is T {
  return v != null
}

export function Step1ClientSearch({ onNext }: { onNext: () => void }) {
  const {
    draft,
    setSelectedExistingClientId,
    setSelectedExistingClientDisplayName,
    setSelectedJobSiteId,
    setIsNewJobSite,
    setIsNewClient,
  } = useProjectCreateWizard()
  const [query, setQuery] = useState('')

  const { results, isLoading, error } = useClientSearchV2({ q: query, take: 20 })

  const normalizedResults = useMemo(() => {
    return results
      .map((r) => {
        const customerId = r.customerId || ''
        const displayName = r.name || ''
        if (!customerId || !displayName) return null

        const primaryContact = (r.contacts || []).find((c) => c?.isPrimary) || (r.contacts || [])[0] || null

        const lastJobSites = (r.lastJobSites || [])
          .map((js) => {
            const id = js.jobSiteId || js.id || ''
            if (!id) return null
            return {
              id,
              label: js.label || '',
              address: js.address || '',
            }
          })
          .filter(isNonNull)

        return {
          customerId,
          displayName,
          phone: primaryContact?.phone || '',
          lastJobSites
        }
      })
      .filter(isNonNull)
  }, [results])

  const handleSelectExisting = (customerId: string, displayName: string) => {
    setSelectedExistingClientId(customerId)
    setSelectedExistingClientDisplayName(displayName)
    setIsNewClient(false)
    setSelectedJobSiteId(null)
    setIsNewJobSite(false)
    onNext()
  }

  const handleCreateNew = () => {
    setSelectedExistingClientId(null)
    setSelectedExistingClientDisplayName(null)
    setIsNewClient(true)
    onNext()
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">Rechercher un client</h2>
        <p className="text-muted-foreground">Nom, téléphone ou adresse du chantier</p>
      </div>

      <div className="max-w-2xl mx-auto space-y-4">
        <div className="space-y-2">
          <Label>Rechercher</Label>
          <Input data-testid="client-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Ex: Dupont, 0475..." />
        </div>

        {error && (
          <div className="rounded-lg border bg-muted/30 p-4">
            <p className="text-sm text-muted-foreground">Erreur lors de la recherche</p>
          </div>
        )}

        {query.trim().length > 0 && (
          <div className="space-y-2">
            {isLoading ? (
              <div className="rounded-lg border bg-muted/30 p-4">
                <p className="text-sm text-muted-foreground">Recherche...</p>
              </div>
            ) : normalizedResults.length > 0 ? (
              normalizedResults.map((c) => {
                const isSelected = draft.selectedExistingClientId === c.customerId
                return (
                  <button
                    key={c.customerId}
                    type="button"
                    data-testid="client-search-result"
                    onClick={() => handleSelectExisting(c.customerId, c.displayName)}
                    className={
                      `w-full text-left px-4 py-3 border rounded-lg transition-colors ` +
                      (isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary')
                    }
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="font-medium truncate">{c.displayName}</p>
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm text-muted-foreground">{c.phone}</p>
                          <Link
                            to={`/clients/${c.customerId}`}
                            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                            onClick={(e) => {
                              e.stopPropagation()
                            }}
                          >
                            <Eye className="h-3.5 w-3.5" />
                            <span>Voir fiche</span>
                          </Link>
                        </div>
                        {c.lastJobSites.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {c.lastJobSites.slice(0, 2).map((js) => (
                              <p key={js.id} className="text-sm text-muted-foreground truncate">
                                🏠 {js.label} — {js.address}
                              </p>
                            ))}
                          </div>
                        )}
                       
                      </div>
                      {isSelected && (
                        <div className="mt-0.5 w-5 h-5 bg-primary rounded-full flex items-center justify-center shrink-0">
                          <span className="text-white text-xs">✓</span>
                        </div>
                      )}
                    </div>
                  </button>
                )
              })
            ) : (
              <div className="rounded-lg border bg-muted/30 p-4">
                <p className="text-sm text-muted-foreground">Aucun résultat.</p>
                <div className="mt-3">
                  <Button type="button" data-testid="client-new-btn" onClick={handleCreateNew}>
                    + Créer nouveau client "{query.trim()}"
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {query.trim().length === 0 && (
          <div className="rounded-lg border bg-muted/30 p-4 flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">Le client est nouveau ?</p>
            <Button type="button" data-testid="client-new-btn" onClick={handleCreateNew}>
              + Nouveau client
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
